import ReactGA from 'react-ga4';

// Analytics configuration
const ANALYTICS_CONFIG = {
  measurementId: process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-KC9HY9YNBR',
  debugMode: process.env.NODE_ENV === 'development',
  appName: process.env.REACT_APP_APP_NAME || 'Unit Converter',
  appVersion: process.env.REACT_APP_APP_VERSION || '1.0.0',
};

// Initialize Google Analytics with enhanced configuration
export const initAnalytics = (measurementId?: string) => {
  const id = measurementId || ANALYTICS_CONFIG.measurementId;
  
  console.log('🔍 initAnalytics called with ID:', id);
  console.log('🔍 Debug mode:', ANALYTICS_CONFIG.debugMode);
  
  if (id && id !== 'G-XXXXXXXXXX') {
    try {
      console.log('🔍 Initializing ReactGA...');
      ReactGA.initialize(id, {
        testMode: ANALYTICS_CONFIG.debugMode,
        gaOptions: {
          siteSpeedSampleRate: 100,
          cookieFlags: 'SameSite=None;Secure',
        },
      });
      console.log('✅ ReactGA initialized successfully');

      // Set user properties
      ReactGA.set({
        app_name: ANALYTICS_CONFIG.appName,
        app_version: ANALYTICS_CONFIG.appVersion,
      });
      console.log('✅ User properties set');

      // Track app initialization
      ReactGA.event({
        category: 'App',
        action: 'Initialize',
        label: ANALYTICS_CONFIG.appVersion,
      });
      console.log('✅ App initialization event sent');

    } catch (error) {
      console.error('❌ Error initializing Google Analytics:', error);
    }
  } else {
    console.warn('⚠️ Google Analytics Measurement ID not configured');
  }
};

// Enhanced page view tracking
export const trackPageView = (path: string, title?: string) => {
  console.log('🔍 trackPageView called:', { path, title, hitType: "pageview" });
  
  try {
    ReactGA.send({ 
      hitType: "pageview", 
      page: path,
      title: title || document.title,
    });
    console.log('✅ Page view sent to GA');
  } catch (error) {
    console.error('❌ Error sending page view to GA:', error);
  }
};

// Enhanced conversion tracking with detailed parameters
export const trackConversion = (category: string, fromUnit: string, toUnit: string, value: string) => {
  const numValue = parseFloat(value) || 0;
  
  ReactGA.event({
    category: 'Unit Conversion',
    action: 'Convert',
    label: `${category}: ${fromUnit} to ${toUnit}`,
    value: numValue,
  });

  // Track conversion frequency
  ReactGA.event({
    category: 'User Behavior',
    action: 'Conversion Frequency',
    label: category,
    value: 1,
  });
};

// Enhanced category change tracking
export const trackCategoryChange = (category: string) => {
  ReactGA.event({
    category: 'Unit Converter',
    action: 'Category Change',
    label: category,
  });

  // Set user property for preferred category
  ReactGA.set({
    preferred_category: category,
  });
};

// Enhanced unit swap tracking
export const trackUnitSwap = (category: string, fromUnit: string, toUnit: string) => {
  ReactGA.event({
    category: 'Unit Converter',
    action: 'Unit Swap',
    label: `${category}: ${fromUnit} ↔ ${toUnit}`,
  });
};

// Enhanced date calculation tracking
export const trackDateCalculation = (startDate: Date, endDate: Date, days: number) => {
  const dateRange = Math.abs(days);
  const isFuture = days > 0;
  const isPast = days < 0;
  
  ReactGA.event({
    category: 'Date Calculator',
    action: 'Calculate Days',
    label: `${days} days`,
    value: dateRange,
  });
};

// Enhanced app usage tracking
export const trackAppUsage = (action: string, label?: string, value?: number) => {
  ReactGA.event({
    category: 'App Usage',
    action: action,
    label: label,
    value: value,
  });
};

// Track user preferences
export const trackUserPreference = (preference: string, value: string) => {
  ReactGA.event({
    category: 'User Preferences',
    action: 'Set Preference',
    label: `${preference}: ${value}`,
  });

  // Set user property
  ReactGA.set({
    [preference]: value,
  });
};

// Track consent changes
export const trackConsentChange = (consent: boolean) => {
  ReactGA.event({
    category: 'Privacy',
    action: 'Consent Change',
    label: consent ? 'Consent Given' : 'Consent Withdrawn',
  });

  // Set user property
  ReactGA.set({
    consent_status: consent ? 'granted' : 'denied',
  });
};

// Track theme changes
export const trackThemeChange = (theme: 'light' | 'dark') => {
  ReactGA.event({
    category: 'User Preferences',
    action: 'Theme Change',
    label: theme,
  });

  // Set user property
  ReactGA.set({
    theme_preference: theme,
  });
};

// Track errors
export const trackError = (error: string, context?: string) => {
  ReactGA.event({
    category: 'Error',
    action: 'App Error',
    label: error,
  });
};

// Track performance metrics
export const trackPerformance = (metric: string, value: number) => {
  ReactGA.event({
    category: 'Performance',
    action: 'Metric',
    label: metric,
    value: Math.round(value),
  });
};

// Track user engagement
export const trackEngagement = (action: string, duration?: number) => {
  ReactGA.event({
    category: 'User Engagement',
    action: action,
    value: duration,
  });
};

// Helper function to get current category (you can implement this)
const getCurrentCategory = (): string => {
  // This could be stored in localStorage or state
  return localStorage.getItem('unit-converter-category') || 'length';
};

// Helper function to get session duration
const getSessionDuration = (): number => {
  const sessionStart = sessionStorage.getItem('session_start');
  if (sessionStart) {
    return Math.round((Date.now() - parseInt(sessionStart)) / 1000);
  }
  return 0;
};

// Initialize session tracking
export const initSessionTracking = () => {
  if (!sessionStorage.getItem('session_start')) {
    sessionStorage.setItem('session_start', Date.now().toString());
    
    ReactGA.event({
      category: 'Session',
      action: 'Start',
    });
  }
};

// Track session end
export const trackSessionEnd = () => {
  const sessionStart = sessionStorage.getItem('session_start');
  if (sessionStart) {
    const duration = Math.round((Date.now() - parseInt(sessionStart)) / 1000);
    
    ReactGA.event({
      category: 'Session',
      action: 'End',
      value: duration,
    });
    
    sessionStorage.removeItem('session_start');
  }
}; 